import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCheckCircle, FiEdit3, FiPlus, FiSearch, FiSettings, FiShield, FiTrash2, FiUsers } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import { hasPurchasingSettingsPermission } from "../../../utils/purchasingSettingsPermissions";
import POApprovalDialog from "../popups/POApprovalDialog";
import CatalogApprovalPolicyEditorModal from "./CatalogApprovalPolicyEditorModal";
import { CATALOG_APPROVAL_API, CATALOG_ENTITY_TYPES, catalogMessages, catalogTypeLabel, catalogUserLabel } from "./catalogApprovalMessages";
import { catalogPolicySettingsMessages } from "./catalogPolicySettingsMessages";
import "../../PurchasingUi.css";
import "../tabs/OrdersSettingsTab.css";
import "./CatalogApprovalSettings.css";

const emptyData = () => ({ businessUnitId: null, users: [], policies: [], canUpdate: false, revision: null });
const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
const initials = (name) => String(name).trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");

function groupBy(rules, keyFor) {
  const groups = new Map();
  for (const rule of rules) {
    const key = keyFor(rule);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(rule);
  }
  return [...groups];
}

function groupPolicyRules(rules) {
  return groupBy(rules, (rule) => {
    const approvers = (rule.approver_ids || []).map(Number).sort((a, b) => a - b);
    return JSON.stringify([rule.enabled, rule.mode, approvers]);
  });
}

export default function CatalogCreationApprovalSettingsTab({ businessUnitId: providedBusinessUnitId }) {
  const { themeId, languageId } = usePmsTheme();
  const t = catalogMessages(languageId);
  const copy = catalogPolicySettingsMessages(languageId);
  const [, refreshSession] = useState(0);
  const sessionUser = readStoredUser();
  const canView = hasPurchasingSettingsPermission(sessionUser, "catalog", "view");
  const canUpdateRole = canView && hasPurchasingSettingsPermission(sessionUser, "catalog", "update");
  const businessUnitId = Number(providedBusinessUnitId ?? sessionUser?.active_business_unit_id) || null;
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [query, setQuery] = useState("");
  const [editor, setEditor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const version = useRef(0);
  const mutationLock = useRef(false);
  const scope = useRef(businessUnitId);
  scope.current = businessUnitId;

  useEffect(() => {
    const update = () => refreshSession((version) => version + 1);
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => { window.removeEventListener("palantir-session-updated", update); window.removeEventListener("storage", update); };
  }, []);

  const load = useCallback(async () => {
    const requestVersion = ++version.current;
    setLoading(canView && Boolean(businessUnitId)); setLoadError(false); setData(emptyData());
    setSaveError(null); setConflict(false); setNotice(false); setEditor(null); setDeleteTarget(null);
    if (!canView || !businessUnitId) return;
    try {
      const { data: response } = await axios.get(`${CATALOG_APPROVAL_API}/settings`, { params: { business_unit_id: businessUnitId } });
      if (requestVersion !== version.current || scope.current !== businessUnitId) return;
      if (!Array.isArray(response?.policies) || !Array.isArray(response?.users) || typeof response.revision !== "string") throw new Error("Invalid settings response");
      setData({ businessUnitId, canUpdate: response.can_update === true, users: response.users, policies: response.policies, revision: response.revision });
    } catch (_) {
      if (requestVersion === version.current && scope.current === businessUnitId) setLoadError(true);
    } finally {
      if (requestVersion === version.current && scope.current === businessUnitId) setLoading(false);
    }
  }, [businessUnitId, canView]);

  useEffect(() => {
    mutationLock.current = false; setSaving(false); setQuery("");
    load();
    return () => { version.current += 1; };
  }, [load]);

  const ready = canView && !loading && !loadError && Boolean(businessUnitId) && data.businessUnitId === businessUnitId;
  const canUpdate = canUpdateRole && data.canUpdate;
  const locked = !ready || saving || !canUpdate || conflict;
  const canMutate = () => !locked && hasPurchasingSettingsPermission(readStoredUser(), "catalog", "view") && hasPurchasingSettingsPermission(readStoredUser(), "catalog", "update");
  const usersById = useMemo(() => new Map(data.users.map((user) => [Number(user.id), user])), [data.users]);
  const userName = (id) => catalogUserLabel(usersById.get(Number(id)) || { id });
  const typeName = (type) => catalogTypeLabel(type, t);
  const policiesByType = useMemo(() => new Map(data.policies.map((policy) => [policy.entity_type, policy])), [data.policies]);
  const rows = useMemo(() => {
    const byUser = new Map();
    const ensure = (id) => {
      const userId = Number(id);
      if (!byUser.has(userId)) byUser.set(userId, { userId, creation: [], approval: [] });
      return byUser.get(userId);
    };
    data.policies.forEach((policy) => (policy.creators || []).forEach((creator) => {
      const rule = { ...creator, entity_type: policy.entity_type, enabled: policy.enabled };
      ensure(creator.user_id).creation.push(rule);
      if (creator.mode === "approval") (creator.approver_ids || []).forEach((id) => ensure(id).approval.push(rule));
    }));
    return [...byUser.values()].map((row) => ({
      ...row,
      creationGroups: groupPolicyRules(row.creation),
      requesters: groupBy(row.approval, (rule) => Number(rule.user_id))
        .map(([id, rules]) => ({ id, groups: groupPolicyRules(rules) })),
    })).sort((a, b) => catalogUserLabel(usersById.get(a.userId) || { id: a.userId }).localeCompare(catalogUserLabel(usersById.get(b.userId) || { id: b.userId }), languageId));
  }, [data.policies, usersById, languageId]);
  const visible = rows.filter((row) => normalize([
    userName(row.userId), usersById.get(row.userId)?.email,
    ...row.creation.flatMap((rule) => [typeName(rule.entity_type), t(rule.mode === "direct" ? "Direct creation" : "Requires approval"), ...(rule.approver_ids || []).map(userName)]),
    ...row.approval.flatMap((rule) => [typeName(rule.entity_type), userName(rule.user_id)]),
  ].join(" ")).includes(normalize(query)));
  const creationCount = rows.reduce((count, row) => count + row.creation.length, 0);
  const assignmentCount = rows.reduce((count, row) => count + row.approval.length, 0);
  const blockedTypes = data.policies.filter((policy) => policy.enabled && !policy.creators?.length).map((policy) => typeName(policy.entity_type));

  const errorMessage = (error) => {
    if (error?.status === 403) return t("You no longer have permission for this action.");
    if (error?.code === "CATALOG_POLICY_REVISION_CONFLICT") return copy.conflict;
    if (error?.code === "CATALOG_APPROVERS_REQUIRED") return copy.lastApprover;
    if (error?.code === "CATALOG_SELF_APPROVAL") return copy.selfApproval;
    if (error?.code === "CATALOG_APPROVAL_RULE_REQUIRED") return copy.approvalRule;
    if (["CATALOG_PARTICIPANT_INVALID", "CATALOG_APPROVER_PERMISSION_REQUIRED", "CATALOG_CREATE_PERMISSION_REQUIRED"].includes(error?.code)) return copy.invalidParticipant;
    return copy.saveFailed;
  };
  const mutate = async (method, path, payload) => {
    if (mutationLock.current || !canMutate()) return false;
    const requestVersion = version.current;
    mutationLock.current = true; setSaving(true); setSaveError(null); setNotice(false);
    try {
      const body = { ...payload, expected_revision: data.revision };
      const options = { params: { business_unit_id: businessUnitId } };
      const response = method === "delete"
        ? await axios.delete(`${CATALOG_APPROVAL_API}${path}`, { ...options, data: body })
        : await axios.put(`${CATALOG_APPROVAL_API}${path}`, body, options);
      if (requestVersion !== version.current || scope.current !== businessUnitId) return false;
      if (!Array.isArray(response.data?.policies) || typeof response.data.revision !== "string") throw new Error("Invalid policy response");
      setData((previous) => ({ ...previous, policies: response.data.policies, revision: response.data.revision }));
      setNotice(true);
      return true;
    } catch (error) {
      if (requestVersion === version.current && scope.current === businessUnitId) {
        const code = error.response?.data?.code;
        setSaveError({ code, status: error.response?.status });
        if (code === "CATALOG_POLICY_REVISION_CONFLICT" || error.response?.status === 409) setConflict(true);
        if (error.response?.status === 403) setData((previous) => ({ ...previous, canUpdate: false }));
      }
      return false;
    } finally {
      if (requestVersion === version.current && scope.current === businessUnitId) { mutationLock.current = false; setSaving(false); }
    }
  };
  const saveUser = (mode, userId, payload) => mutate("put", `/settings/users/${userId}`, { mode, ...payload });
  const toggleType = (type, enabled) => mutate("put", `/settings/${type}`, { enabled, creators: policiesByType.get(type)?.creators || [] });
  const openEditor = (userId = null) => { if (canMutate()) { setSaveError(null); setNotice(false); setEditor({ userId }); } };
  const deletePolicy = async () => { if (deleteTarget && await mutate("delete", `/settings/users/${deleteTarget.userId}`, {})) setDeleteTarget(null); };
  const feedback = saveError && <><span>{conflict ? copy.conflict : errorMessage(saveError)}</span>{(conflict || saveError.status === 403) && <button type="button" className="poal-button" disabled={saving} onClick={load}>{copy.reload}</button>}</>;

  const renderRuleGroup = ([key, rules], showMode) => {
    const { mode, enabled, approver_ids: approvers = [] } = rules[0];
    return <div className="poal-rule" key={key}>
      {(showMode || !enabled) && <div className="poal-ruleHeading">
        {showMode && <strong>{t(mode === "direct" ? "Direct creation" : "Requires approval")}</strong>}
        {!enabled && <span className="poal-inactive">{copy.inactive}</span>}
      </div>}
      <ul className="catalogPolicySettings__typeList">
        {rules.map(({ entity_type }) => <li key={entity_type}>{typeName(entity_type)}</li>)}
      </ul>
      {mode === "approval" && (showMode || approvers.length > 1) &&
        <small>{copy.approvers}: {approvers.map(userName).join(", ")}</small>}
    </div>;
  };

  if (!canView) return <p role="alert">{t("You no longer have permission for this action.")}</p>;

  return <section className="pilcore-purchasing-ui poal-shell catalogPolicySettings" data-theme={themeId} aria-label={copy.title}>
    <header className="poal-hero">
      <div><h2><FiShield aria-hidden="true" />{copy.title}</h2><p>{copy.subtitle}</p></div>
      <button className="poal-button poal-button--primary" type="button" onClick={() => openEditor()} disabled={locked}><FiPlus aria-hidden="true" />{copy.newPolicy}</button>
    </header>
    {!ready ? loading ? <div className="poal-empty" role="status">{t("Loading…")}</div> : !businessUnitId ? <div className="poal-empty">{copy.missingUnit}</div> : <div className="poal-empty"><p role="alert">{t("Settings could not be loaded.")}</p><button type="button" className="poal-button" onClick={load}>{t("Retry")}</button></div> : <>
      {!canUpdate && <p className="poal-note">{t("You can view these policies, but your role cannot change them.")}</p>}
      <section className="poal-global catalogPolicySettings__scope" aria-label={copy.scope}>
        <FiSettings className="poal-globalIcon" aria-hidden="true" />
        <div className="poal-globalCopy"><strong>{copy.scope}</strong>
          <div className="catalogPolicySettings__types">{CATALOG_ENTITY_TYPES.map((type) => <label key={type} className="poal-check">
            <input type="checkbox" aria-label={typeName(type)} aria-describedby="catalog-policy-scope-help" checked={Boolean(policiesByType.get(type)?.enabled)} disabled={locked} onChange={(event) => toggleType(type, event.target.checked)} />{typeName(type)}
          </label>)}</div>
          <small id="catalog-policy-scope-help">{copy.scopeHelp}</small>
          {blockedTypes.length > 0 && <small className="catalogPolicySettings__blocked">{copy.emptyEnabled}: {blockedTypes.join(", ")}.</small>}
        </div>
      </section>
      <div className="poal-toolbar">
        <label className="poal-search"><FiSearch aria-hidden="true" /><input type="search" aria-label={copy.search} placeholder={copy.search} value={query} onChange={(event) => setQuery(event.target.value)} /></label>
        <div className="poal-summary"><span><strong>{rows.length}</strong> {copy.users}</span><span><strong>{creationCount}</strong> {copy.rules}</span><span><strong>{assignmentCount}</strong> {copy.assignments}</span></div>
      </div>
      {saveError && !editor && !deleteTarget && <div className="poal-dialogError" role="alert">{feedback}</div>}
      {notice && !editor && !deleteTarget && <div className="catalogPolicySettings__saved" role="status"><FiCheckCircle aria-hidden="true" />{t("Policy saved.")}</div>}
      {!rows.length ? <div className="poal-empty"><FiUsers aria-hidden="true" /><strong>{copy.noPolicies}</strong><p>{copy.noPoliciesHelp}</p></div> : !visible.length ? <div className="poal-empty"><p>{copy.noMatches}</p><button type="button" className="poal-button" onClick={() => setQuery("")}>{copy.clear}</button></div> : <div className="poal-listViewport">
        <table className="poal-table"><colgroup><col className="poal-userColumn" /><col /><col /><col className="poal-actionsColumn" /></colgroup><thead><tr><th>{copy.user}</th><th>{copy.creation}</th><th>{copy.approval}</th><th>{copy.actions}</th></tr></thead>
          <tbody>{visible.map((row) => <tr key={row.userId}>
            <th scope="row"><div className="poal-user"><span className="poal-avatar" aria-hidden="true">{initials(userName(row.userId))}</span><div><strong>{userName(row.userId)}</strong><small>{usersById.get(row.userId)?.email}</small>{usersById.get(row.userId)?.is_active === false && <small>{copy.inactiveUser}</small>}</div></div></th>
            <td data-label={copy.creation}><div className="poal-ruleCell">{row.creationGroups.length
              ? row.creationGroups.map((group) => renderRuleGroup(group, true))
              : <span className="poal-fallback">{copy.noCreation}</span>}</div></td>
            <td data-label={copy.approval}><div className="poal-ruleCell">{row.requesters.length
              ? row.requesters.map(({ id, groups }) => <div key={id} className="catalogPolicySettings__requester">
                <div className="poal-ruleHeading"><strong>{copy.requester}: {userName(id)}</strong></div>
                {groups.map((group) => renderRuleGroup(group, false))}
              </div>)
              : <span className="poal-fallback">{copy.noApproval}</span>}</div></td>
            <td><div className="poal-rowActions"><button type="button" className="poal-iconButton" aria-label={`${copy.edit} ${userName(row.userId)}`} title={`${copy.edit} ${userName(row.userId)}`} disabled={locked} onClick={() => openEditor(row.userId)}><FiEdit3 aria-hidden="true" /></button><button type="button" className="poal-iconButton poal-iconButton--danger" aria-label={`${copy.remove} ${userName(row.userId)}`} title={`${copy.remove} ${userName(row.userId)}`} disabled={locked} onClick={() => { if (canMutate()) { setSaveError(null); setDeleteTarget(row); } }}><FiTrash2 aria-hidden="true" /></button></div></td>
          </tr>)}</tbody>
        </table>
      </div>}
      <div className="poal-count catalogPolicySettings__footer">
        {assignmentCount > 0 && <span>{copy.required}.</span>}
        <span>{copy.shown} {visible.length} {copy.of} {rows.length} {copy.users}</span>
      </div>
    </>}
    {ready && editor && <CatalogApprovalPolicyEditorModal isOpen onClose={() => { if (!saving) { setEditor(null); if (!conflict && data.canUpdate) setSaveError(null); } }} initialUserId={editor.userId} users={data.users} policies={data.policies} onSave={saveUser} busy={saving} readOnly={!canUpdate || conflict} error={feedback} language={languageId} />}
    <POApprovalDialog isOpen={ready && Boolean(deleteTarget)} onClose={() => { if (!saving) { setDeleteTarget(null); if (!conflict && data.canUpdate) setSaveError(null); } }} busy={saving} width="narrow" title={copy.deleteTitle}
      footer={<><button className="poal-button" type="button" disabled={saving} onClick={() => { setDeleteTarget(null); if (!conflict && data.canUpdate) setSaveError(null); }}>{copy.cancel}</button><button className="poal-button poal-button--danger" type="button" disabled={locked} onClick={deletePolicy}>{saving ? t("Saving…") : copy.delete}</button></>}>
      <p className="poal-dialogCopy">{copy.deleteHelp}</p><strong>{deleteTarget && userName(deleteTarget.userId)}</strong>{saveError && <div className="poal-dialogError" role="alert">{feedback}</div>}
    </POApprovalDialog>
  </section>;
}
